import { createActions } from 'reduxsauce'

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  // Fetch all plans
  plansFetchRequest: null,
  plansFetchSuccess: ['data'],
  plansFetchFailure: ['error'],

  // Fetch current plan details
  plansCurrentRequest: null,
  plansCurrentSuccess: ['data'],
  plansCurrentFailure: ['error'],

  // Upgrade plan
  plansUpgradeRequest: ['planKey'],
  plansUpgradeSuccess: ['data'],
  plansUpgradeFailure: ['error'],

  // Subscribe (get Stripe checkout URL)
  plansSubscribeRequest: ['baseUrl', 'billingCycle'],
  plansSubscribeSuccess: ['data'],
  plansSubscribeFailure: ['error'],

  // Downgrade (cancel subscription)
  plansDowngradeRequest: null,
  plansDowngradeSuccess: ['data'],
  plansDowngradeFailure: ['error'],

  // Reactivate (undo downgrade)
  plansReactivateRequest: null,
  plansReactivateSuccess: ['data'],
  plansReactivateFailure: ['error'],

  // Compare plans
  plansCompareRequest: null,
  plansCompareSuccess: ['data'],
  plansCompareFailure: ['error'],

  // Buy credit top-up
  plansBuyCreditsRequest: ['packId', 'baseUrl'],
  plansBuyCreditsSuccess: ['data'],
  plansBuyCreditsFailure: ['error'],

  // Clear plans (on logout)
  plansClear: null,
})

export const PlansTypes = Types
export default Creators

/* ------------- Initial State ------------- */

const INITIAL_STATE = {
  data: null,
  fetching: false,
  error: null,

  currentData: null,
  currentFetching: false,
  currentError: null,

  upgradeData: null,
  upgrading: false,
  upgradeError: null,

  subscribeData: null,
  subscribing: false,
  subscribeError: null,

  downgradeData: null,
  downgrading: false,
  downgradeError: null,

  reactivateData: null,
  reactivating: false,
  reactivateError: null,

  compareData: null,
  comparing: false,
  compareError: null,
}

/* ------------- Stable Default Values ------------- */

const EMPTY_ARRAY = []

/* ------------- Selectors ------------- */

export const PlansSelectors = {
  getData: (state) => state.plans.data,
  getPlans: (state) => (state.plans.data && state.plans.data.plans) || EMPTY_ARRAY,
  getCurrentPlan: (state) => (state.plans.data && state.plans.data.current_plan) || null,
  isFetching: (state) => state.plans.fetching,
  getError: (state) => state.plans.error,

  getCurrentData: (state) => state.plans.currentData,
  getCurrentPlanDetails: (state) => (state.plans.currentData && state.plans.currentData.details) || state.plans.currentData || null,
  isCurrentFetching: (state) => state.plans.currentFetching,
  getCurrentError: (state) => state.plans.currentError,

  getUpgradeData: (state) => state.plans.upgradeData,
  isUpgrading: (state) => state.plans.upgrading,
  getUpgradeError: (state) => state.plans.upgradeError,

  getSubscribeData: (state) => state.plans.subscribeData,
  isSubscribing: (state) => state.plans.subscribing,
  getSubscribeError: (state) => state.plans.subscribeError,

  getDowngradeData: (state) => state.plans.downgradeData,
  isDowngrading: (state) => state.plans.downgrading,
  getDowngradeError: (state) => state.plans.downgradeError,

  getReactivateData: (state) => state.plans.reactivateData,
  isReactivating: (state) => state.plans.reactivating,
  getReactivateError: (state) => state.plans.reactivateError,

  getSubscriptionStatus: (state) => (state.plans.currentData && state.plans.currentData.subscription_status) || 'inactive',
  getSubscriptionEndsAt: (state) => (state.plans.currentData && state.plans.currentData.subscription_ends_at) || null,
  isSubscriptionCanceling: (state) => (state.plans.currentData && state.plans.currentData.subscription_canceling) || false,

  getCompareData: (state) => state.plans.compareData,
  isComparing: (state) => state.plans.comparing,
  getCompareError: (state) => state.plans.compareError,
}

/* ------------- Reducer ------------- */

export const reducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    // Fetch Plans
    case Types.PLANS_FETCH_REQUEST:
      return { ...state, fetching: true, error: null }
    case Types.PLANS_FETCH_SUCCESS:
      return { ...state, data: action.data, fetching: false, error: null }
    case Types.PLANS_FETCH_FAILURE:
      return { ...state, fetching: false, error: action.error }

    // Fetch Current Plan
    case Types.PLANS_CURRENT_REQUEST:
      return { ...state, currentFetching: true, currentError: null }
    case Types.PLANS_CURRENT_SUCCESS:
      return { ...state, currentData: action.data, currentFetching: false, currentError: null }
    case Types.PLANS_CURRENT_FAILURE:
      return { ...state, currentFetching: false, currentError: action.error }

    // Upgrade Plan
    case Types.PLANS_UPGRADE_REQUEST:
      return { ...state, upgrading: true, upgradeError: null, upgradeData: null }
    case Types.PLANS_UPGRADE_SUCCESS:
      return { ...state, upgradeData: action.data, upgrading: false, upgradeError: null }
    case Types.PLANS_UPGRADE_FAILURE:
      return { ...state, upgrading: false, upgradeError: action.error }

    // Subscribe
    case Types.PLANS_SUBSCRIBE_REQUEST:
      return { ...state, subscribing: true, subscribeError: null, subscribeData: null }
    case Types.PLANS_SUBSCRIBE_SUCCESS:
      return { ...state, subscribeData: action.data, subscribing: false, subscribeError: null }
    case Types.PLANS_SUBSCRIBE_FAILURE:
      return { ...state, subscribing: false, subscribeError: action.error }

    // Downgrade
    case Types.PLANS_DOWNGRADE_REQUEST:
      return { ...state, downgrading: true, downgradeError: null, downgradeData: null }
    case Types.PLANS_DOWNGRADE_SUCCESS:
      return { ...state, downgradeData: action.data, downgrading: false, downgradeError: null }
    case Types.PLANS_DOWNGRADE_FAILURE:
      return { ...state, downgrading: false, downgradeError: action.error }

    // Reactivate
    case Types.PLANS_REACTIVATE_REQUEST:
      return { ...state, reactivating: true, reactivateError: null, reactivateData: null }
    case Types.PLANS_REACTIVATE_SUCCESS:
      return { ...state, reactivateData: action.data, reactivating: false, reactivateError: null }
    case Types.PLANS_REACTIVATE_FAILURE:
      return { ...state, reactivating: false, reactivateError: action.error }

    // Compare Plans
    case Types.PLANS_COMPARE_REQUEST:
      return { ...state, comparing: true, compareError: null }
    case Types.PLANS_COMPARE_SUCCESS:
      return { ...state, compareData: action.data, comparing: false, compareError: null }
    case Types.PLANS_COMPARE_FAILURE:
      return { ...state, comparing: false, compareError: action.error }

    // Clear
    case Types.PLANS_CLEAR:
      return { ...INITIAL_STATE }

    default:
      return state
  }
}
